import { IProductDetails } from '@models/IProductDetails';
import { IProductPageContent } from '@models/IProductPageContent';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import getDynamicContent from '@services/dynamicContent';
import getProductDetails from '@services/productDetails';
import getProductRelations from '@services/productRelations';

interface Props {
  materialCode: string;
  updateUrl?: boolean;
  endpoints: {
    getProductDetailsByMaterialCodeEndpoint: string;
    getRelationsByMaterialCodeEndpoint: string;
    getDynamicContentByMaterialCodeEndpoint: string;
  };
  language: string;
  onSetIsLoading: (value: boolean) => void;
  onSetProductDetails: (data: IProductDetails) => void;
  onSetProductPageContent: (data: IProductPageContent) => void;
  onSetErrorBannerNotification?: (
    errorNotificationDisplay: ErrorBannerNotificationEnum | null
  ) => void;
}

export const getProductData = async ({
  materialCode,
  updateUrl = false,
  endpoints,
  language,
  onSetIsLoading,
  onSetProductDetails,
  onSetProductPageContent,
  onSetErrorBannerNotification,
}: Readonly<Props>) => {
  try {
    onSetIsLoading(true);

    const data = await getProductDetails({
      getProductDetailsByMaterialCodeEndpoint:
        endpoints.getProductDetailsByMaterialCodeEndpoint,
      language,
      materialCode,
    });

    onSetProductDetails(data);

    const [relationData, dynamicContentData] = await Promise.all([
      getProductRelations({
        getRelationsByMaterialCodeEndpoint:
          endpoints.getRelationsByMaterialCodeEndpoint,
        language,
        materialCode,
      }),
      getDynamicContent({
        getDynamicContentByMaterialCodeEndpoint:
          endpoints.getDynamicContentByMaterialCodeEndpoint,
        language,
        materialCode,
      }),
    ]);

    document.title = data.seoTitle;

    onSetProductPageContent({ ...dynamicContentData, ...relationData });

    if (updateUrl && window.location.pathname !== data.variationUrl) {
      window.history.pushState({ code: data.code }, '', data.variationUrl);
    }
  } catch (err) {
    onSetErrorBannerNotification?.(
      ErrorBannerNotificationEnum.ProductDetailsFetch
    );

    // biome-ignore lint/suspicious/noConsole: Allow error logging
    console.error('Failed to load product data:', err);
  } finally {
    onSetIsLoading(false);
  }
};
